/**
 * Status SignalR Service
 * สำหรับ connect และรับ AI status updates จาก backend
 * (Simple version - single status message)
 */
import * as signalR from "@microsoft/signalr";

class StatusService {
  constructor() {
    this.connection = null;
    this.isConnected = false;
    this.isConnecting = false;
    this.connectionPromise = null;
    this.urlApi = import.meta.env.VITE_APP_API || 'http://localhost:5000/';
    
    // Event handlers
    this.statusHandlers = new Map();
  }

  /**
   * Connect to Status SignalR hub
   * @param {string} token - Auth token (optional)
   * @returns {Promise<signalR.HubConnection>}
   */
  async connect(token = null) {
    if (this.connection && this.isConnected) {
      return this.connection;
    }

    if (this.isConnecting && this.connectionPromise) {
      return this.connectionPromise;
    }

    this.isConnecting = true;
    this.connectionPromise = this._createConnection(token);

    try {
      const connection = await this.connectionPromise;
      this.isConnecting = false;
      return connection;
    } catch (error) {
      this.isConnecting = false;
      this.connectionPromise = null;
      throw error;
    }
  }

  async _createConnection(token) {
    const hubUrl = `${this.urlApi}statusHub`;
    
    const builder = new signalR.HubConnectionBuilder()
      .withUrl(hubUrl, {
        transport: signalR.HttpTransportType.WebSockets |
          signalR.HttpTransportType.ServerSentEvents |
          signalR.HttpTransportType.LongPolling,
        accessTokenFactory: () => token
      })
      .withAutomaticReconnect([3000, 5000, 10000, 15000]);

    this.connection = builder.build();
    this.connection.serverTimeoutInMilliseconds = 120000;

    // Setup event handler
    this.connection.on('StatusUpdate', (status) => {
      this._invokeHandlers(status);
    });

    // Connection lifecycle
    this.connection.onclose(() => {
      this.isConnected = false;
    });

    this.connection.onreconnected(() => {
      this.isConnected = true;
    });

    try {
      await this.connection.start();
      this.isConnected = true;
      return this.connection;
    } catch (err) {
      console.error('[StatusService] Connection failed:', err);
      this.isConnected = false;
      throw err;
    }
  }

  _invokeHandlers(status) {
    for (const [, handler] of this.statusHandlers) {
      try {
        handler(status);
      } catch (e) {
        console.error('[StatusService] Handler error:', e);
      }
    }
  }

  /**
   * Join user group to receive updates
   * @param {string} userId 
   */
  async joinUserGroup(userId) {
    if (!this.connection || !this.isConnected) {
      console.error('[StatusService] Not connected');
      return;
    }
    await this.connection.invoke('JoinUserGroup', userId);
  }

  /**
   * Leave user group
   * @param {string} userId 
   */
  async leaveUserGroup(userId) {
    if (!this.connection || !this.isConnected) return;
    await this.connection.invoke('LeaveUserGroup', userId);
  }

  /**
   * Register status update handler
   * @param {string} handlerId - unique ID for this handler
   * @param {Function} callback - function(status) to call on update
   */
  onStatusUpdate(handlerId, callback) {
    this.statusHandlers.set(handlerId, callback);
  }

  /**
   * Unregister handler
   * @param {string} handlerId 
   */
  offStatusUpdate(handlerId) {
    this.statusHandlers.delete(handlerId);
  }

  /**
   * Disconnect from hub
   */
  async disconnect() {
    if (this.connection) {
      try {
        await this.connection.stop();
      } catch (error) {
        console.error('[StatusService] Disconnect error:', error);
      }
      this.isConnected = false;
      this.connection = null;
      this.connectionPromise = null;
    }
  }
}

// Export singleton instance
const statusService = new StatusService();
export default statusService;
